import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { LOCAL_STORAGE } from "@constants/index";

const DEFAULT_LANGUAGE = "ko";

const LanguageSelector = ({ "data-testid": dataTestId = "language-selector" }) => {
  const [currentLang, setCurrentLang] = useState(DEFAULT_LANGUAGE);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredLang, setHoveredLang] = useState(null);
  const dropdownRef = useRef(null);
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();

  const LANGUAGES = [
    { code: "EN", name: t("language.english"), i18nCode: "en" },
    { code: "KO", name: t("language.korean"), i18nCode: "ko" },
    { code: "JA", name: t("language.japanese"), i18nCode: "ja" },
  ];

  // Helper function to map between display and i18n codes
  const getI18nCode = useCallback((displayCode) => {
    return LANGUAGES.find((lang) => lang.code === displayCode)?.i18nCode || displayCode.toLowerCase();
  }, []);

  const getDisplayCode = useCallback((i18nCode) => {
    return LANGUAGES.find((lang) => lang.i18nCode === i18nCode)?.code || i18nCode.toUpperCase();
  }, []);

  // Helper function to get language name from code
  const getLanguageName = useCallback((langCode) => {
    return LANGUAGES.find((lang) => lang.code === langCode)?.name || langCode;
  }, [t]);

  // Initialize language from localStorage or set default
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    const langToUse = savedLanguage && LANGUAGES.some((lang) => lang.i18nCode === savedLanguage) 
      ? getDisplayCode(savedLanguage) 
      : DEFAULT_LANGUAGE;

    setCurrentLang(langToUse);
    i18n.changeLanguage(getI18nCode(langToUse));

    // Don't automatically set localStorage - only set when user explicitly selects
  }, [i18n, getI18nCode, getDisplayCode]);

  // Sync with i18n language changes
  useEffect(() => {
    const displayLang = getDisplayCode(i18n.language);
    if (displayLang !== currentLang) {
      setCurrentLang(displayLang);
    }
  }, [i18n.language, currentLang, getDisplayCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = useCallback(
    (langCode) => {
      const i18nLangCode = getI18nCode(langCode);
      i18n.changeLanguage(i18nLangCode);
      setCurrentLang(langCode);
      localStorage.setItem(LOCAL_STORAGE.LANGUAGE, i18nLangCode);
      setIsDropdownOpen(false);
    },
    [i18n, getI18nCode],
  );

  const toggleDropdown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen((prev) => !prev);
  }, []);

  return (
    <div className="relative flex items-center space-x-2 px-4" ref={dropdownRef} data-testid={dataTestId}>
      {/* Globe Icon */}
      <svg
        className={`h-4 w-4 ${colors.iconMuted}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        data-testid={`${dataTestId}-globe`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>

      {/* Language Button */}
      <button
        type="button"
        className={`flex items-center space-x-1 text-sm font-medium ${colors.sidebarTextPrimary} transition-colors ${colors.textHoverActive}`}
        onClick={toggleDropdown}
        data-testid={`${dataTestId}-toggle`}
      >
        <span data-testid={`${dataTestId}-current-label`}>{getLanguageName(currentLang)}</span>
        <ChevronDownIcon className="h-4 w-4" />
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div 
          data-testid={`${dataTestId}-menu`}
          className="absolute top-full right-0 z-[9999] mt-1 w-32 rounded-[0.25rem] border-t-0 p-[0.125rem]"
          style={{
            backgroundColor: colors.bgPrimaryColor,
            boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)"
          }}
        >
          <div 
            data-testid={`${dataTestId}-menu-inner`}
            className="rounded-[0.5rem] p-2 m-0"
            style={{
              backgroundColor: colors.bgPrimaryColor,
              color: colors.textPrimaryColor
            }}
          >
            {LANGUAGES.map((language, index) => {
              const isSelected = currentLang === language.code;
              const isHovered = hoveredLang === language.code;
              const isActive = isSelected || isHovered;
              const isLast = index === LANGUAGES.length - 1;
              
              return (
                <button
                  key={language.code}
                  type="button"
                  data-testid={`${dataTestId}-option-${language.i18nCode}`}
                  onClick={() => handleLanguageSelect(language.code)}
                  onMouseEnter={() => setHoveredLang(language.code)}
                  onMouseLeave={() => setHoveredLang(null)}
                  className={`w-full text-left text-sm transition-all duration-200 relative rounded-[0.25rem] px-3 py-2 ${
                    !isLast ? 'mb-[3px]' : ''
                  }`}
                  style={{
                    backgroundColor: colors.bgPrimaryColor,
                    color: isActive ? colors.textActiveColor : colors.textPrimaryColor,
                    border: isActive ? `1px solid ${colors.dropdownBorderColor}` : "1px solid transparent",
                    boxShadow: isActive ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
                    zIndex: isActive ? 1000 : 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {language.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
